import React, { useState, useEffect, useCallback } from 'react';
import { hrAPI } from '../../services/api';
import { formatDate, formatCurrency } from '../../utils/helpers';
import StatCard from '../../components/common/StatCard';
import StatusBadge from '../../components/common/StatusBadge';
import Modal from '../../components/common/Modal';
import SearchBar from '../../components/common/SearchBar';
import toast from 'react-hot-toast';

/* ─── colour helpers ─────────────────────────────────────────────────── */
const JOB_STA  = { draft:{bg:'#f1f5f9',c:'#475569'}, published:{bg:'#d1fae5',c:'#065f46'}, closed:{bg:'#fee2e2',c:'#991b1b'}, on_hold:{bg:'#fef3c7',c:'#92400e'}, filled:{bg:'#eff6ff',c:'#1d4ed8'} };
const APP_STA  = { applied:{bg:'#eff6ff',c:'#1d4ed8'}, screening:{bg:'#fef3c7',c:'#92400e'}, shortlisted:{bg:'#fdf4ff',c:'#7c3aed'}, interview_1:{bg:'#fff7ed',c:'#c2410c'}, interview_2:{bg:'#fed7aa',c:'#9a3412'}, technical_test:{bg:'#f0fdf4',c:'#15803d'}, offer_made:{bg:'#dcfce7',c:'#15803d'}, offer_accepted:{bg:'#d1fae5',c:'#065f46'}, joined:{bg:'#a7f3d0',c:'#047857'}, rejected:{bg:'#fee2e2',c:'#991b1b'}, withdrawn:{bg:'#f1f5f9',c:'#475569'} };
const TRN_STA  = { planned:{bg:'#eff6ff',c:'#1d4ed8'}, ongoing:{bg:'#fef3c7',c:'#92400e'}, completed:{bg:'#d1fae5',c:'#065f46'}, cancelled:{bg:'#fee2e2',c:'#991b1b'} };
const POL_CAT  = { leave:'🏖️', conduct:'📋', salary:'💰', it:'💻', grievance:'⚖️', attendance:'✅', recruitment:'🔍', travel:'✈️', other:'📄' };

const Chip = ({ map, val }) => {
  const c = map[val] || { bg: '#f1f5f9', c: '#475569' };
  return <span style={{ background: c.bg, color: c.c, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{(val || '').replace(/_/g, ' ')}</span>;
};

/* ─── blank form objects ─────────────────────────────────────────────── */
const BLANK_JOB = { title: '', department: '', employment_type: 'full_time', vacancies: 1, description: '', responsibilities: '', requirements: '', qualifications: '', experience_min: 0, experience_max: '', salary_from: '', salary_to: '', location: '', status: 'draft', posted_date: new Date().toISOString().split('T')[0], application_deadline: '', skills_required: '', benefits: '' };
const BLANK_APP = { status: 'shortlisted', interview_date: '', interview_mode: 'in_person', score: '', notes: '', rejection_reason: '' };
const BLANK_TRN = { title: '', training_type: 'technical', description: '', objectives: '', trainer_name: '', trainer_type: 'internal', training_provider: '', start_date: '', end_date: '', duration_hours: '', venue: '', mode: 'classroom', max_participants: 20, cost_per_person: 0, status: 'planned', certificate_provided: false, notes: '' };
const BLANK_APR = { staff_id: '', staff_name: '', appraisal_period: '', appraisal_date: new Date().toISOString().split('T')[0], teaching_quality: '', punctuality: '', student_results: '', teamwork: '', communication: '', innovation: '', achievements: '', areas_of_improvement: '', training_recommendations: '', goals_for_next_period: '', increment_recommended: '', promotion_recommended: false };
const BLANK_POL = { title: '', category: 'leave', version: '1.0', content: '', summary: '', applicable_to: 'all', effective_date: '', review_date: '' };

export default function HRManagement() {
  const [tab, setTab]             = useState('recruitment');
  const [jobs, setJobs]           = useState([]);
  const [applications, setApps]   = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [enrollments, setEnrols]  = useState([]);
  const [appraisals, setAppraisals] = useState([]);
  const [policies, setPolicies]   = useState([]);
  const [staffDocs, setStaffDocs] = useState([]);
  const [hrStats, setHrStats]     = useState({});
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);

  /* filters */
  const [search, setSearch]       = useState('');
  const [jobStaF, setJobStaF]     = useState('');
  const [jobDeptF, setJobDeptF]   = useState('');
  const [appStaF, setAppStaF]     = useState('');
  const [trnStaF, setTrnStaF]     = useState('');
  const [polCatF, setPolCatF]     = useState('');

  /* modals */
  const [showJobForm, setShowJobForm]   = useState(false);
  const [showAppForm, setShowAppForm]   = useState(false);
  const [showTrnForm, setShowTrnForm]   = useState(false);
  const [showAprForm, setShowAprForm]   = useState(false);
  const [showPolForm, setShowPolForm]   = useState(false);
  const [editJob, setEditJob]           = useState(null);
  const [editTrn, setEditTrn]           = useState(null);
  const [editPol, setEditPol]           = useState(null);
  const [selectedApp, setSelectedApp]   = useState(null);

  /* form state */
  const [jobForm, setJobForm]   = useState(BLANK_JOB);
  const [appForm, setAppForm]   = useState(BLANK_APP);
  const [trnForm, setTrnForm]   = useState(BLANK_TRN);
  const [aprForm, setAprForm]   = useState(BLANK_APR);
  const [polForm, setPolForm]   = useState(BLANK_POL);

  const JF = (k, v) => setJobForm(f => ({ ...f, [k]: v }));
  const TF = (k, v) => setTrnForm(f => ({ ...f, [k]: v }));
  const AF = (k, v) => setAprForm(f => ({ ...f, [k]: v }));
  const PF = (k, v) => setPolForm(f => ({ ...f, [k]: v }));

  const load = useCallback(() => {
    setLoading(true);
    const jp = {}; if (jobStaF) jp.status = jobStaF; if (jobDeptF) jp.department = jobDeptF; if (search) jp.search = search;
    const ap = {}; if (appStaF) ap.status = appStaF;
    const tp = {}; if (trnStaF) tp.status = trnStaF;
    const pp = {}; if (polCatF) pp.category = polCatF;
    Promise.all([
      hrAPI.jobs(jp), hrAPI.applications(ap), hrAPI.trainings(tp),
      hrAPI.enrollments({}), hrAPI.appraisals({}), hrAPI.policies(pp),
      hrAPI.staffDocs({}), hrAPI.hrStats(),
    ]).then(([j, a, t, e, ap2, pol, docs, st]) => {
      setJobs(j.data.results || j.data);
      setApps(a.data.results || a.data);
      setTrainings(t.data.results || t.data);
      setEnrols(e.data.results || e.data);
      setAppraisals(ap2.data.results || ap2.data);
      setPolicies(pol.data.results || pol.data);
      setStaffDocs(docs.data.results || docs.data);
      setHrStats(st.data || {});
    }).catch(() => toast.error('Failed to load HR data')).finally(() => setLoading(false));
  }, [search, jobStaF, jobDeptF, appStaF, trnStaF, polCatF]);

  useEffect(() => { load(); }, [load]);

  /* ─── save handlers ─────────────────────────────────────────────── */
  const saveJob = async e => {
    e.preventDefault(); setSaving(true);
    try {
      editJob ? await hrAPI.updateJob(editJob.id, jobForm) : await hrAPI.createJob(jobForm);
      toast.success(editJob ? 'Job updated!' : 'Job posted!');
      setShowJobForm(false); setEditJob(null); setJobForm(BLANK_JOB); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  const saveAppUpdate = async e => {
    e.preventDefault(); setSaving(true);
    try {
      await hrAPI.updateApplication(selectedApp.id, appForm);
      toast.success('Application updated!'); setShowAppForm(false); load();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const saveTrn = async e => {
    e.preventDefault(); setSaving(true);
    try {
      editTrn ? await hrAPI.updateTraining(editTrn.id, trnForm) : await hrAPI.createTraining(trnForm);
      toast.success(editTrn ? 'Training updated!' : 'Training program created!');
      setShowTrnForm(false); setEditTrn(null); setTrnForm(BLANK_TRN); load();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const saveApr = async e => {
    e.preventDefault(); setSaving(true);
    try {
      await hrAPI.createAppraisal(aprForm);
      toast.success('Appraisal saved!'); setShowAprForm(false); setAprForm(BLANK_APR); load();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const savePol = async e => {
    e.preventDefault(); setSaving(true);
    try {
      editPol ? await hrAPI.updatePolicy(editPol.id, polForm) : await hrAPI.createPolicy(polForm);
      toast.success(editPol ? 'Policy updated!' : 'Policy created!');
      setShowPolForm(false); setEditPol(null); setPolForm(BLANK_POL); load();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const openEditJob = j => { setEditJob(j); setJobForm({ title: j.title, department: j.department, employment_type: j.employment_type, vacancies: j.vacancies, description: j.description, responsibilities: j.responsibilities || '', requirements: j.requirements, qualifications: j.qualifications || '', experience_min: j.experience_min, experience_max: j.experience_max || '', salary_from: j.salary_from, salary_to: j.salary_to, location: j.location || '', status: j.status, posted_date: j.posted_date, application_deadline: j.application_deadline || '', skills_required: j.skills_required || '', benefits: j.benefits || '' }); setShowJobForm(true); };
  const openAppUpdate = a => { setSelectedApp(a); setAppForm({ status: a.status, interview_date: a.interview_date || '', interview_mode: a.interview_mode || 'in_person', score: a.score || '', notes: a.notes || '', rejection_reason: a.rejection_reason || '' }); setShowAppForm(true); };
  const openEditTrn = t => { setEditTrn(t); setTrnForm({ title: t.title, training_type: t.training_type, description: t.description || '', objectives: t.objectives || '', trainer_name: t.trainer_name, trainer_type: t.trainer_type, training_provider: t.training_provider || '', start_date: t.start_date, end_date: t.end_date, duration_hours: t.duration_hours || '', venue: t.venue || '', mode: t.mode, max_participants: t.max_participants, cost_per_person: t.cost_per_person, status: t.status, certificate_provided: t.certificate_provided, notes: t.notes || '' }); setShowTrnForm(true); };
  const openEditPol = p => { setEditPol(p); setPolForm({ title: p.title, category: p.category, version: p.version, content: p.content, summary: p.summary || '', applicable_to: p.applicable_to, effective_date: p.effective_date, review_date: p.review_date || '' }); setShowPolForm(true); };

  const TABS = [
    { key: 'recruitment', label: '🔍 Recruitment', count: jobs.length },
    { key: 'applications', label: '📄 Applications', count: applications.length },
    { key: 'training', label: '🎓 Training', count: trainings.length },
    { key: 'appraisals', label: '⭐ Appraisals', count: appraisals.length },
    { key: 'policies', label: '📜 Policies', count: policies.length },
    { key: 'documents', label: '📁 Staff Documents', count: staffDocs.length },
  ];

  const statCards = [
    { icon: '📋', label: 'Open Jobs', value: hrStats.open_jobs || jobs.filter(j => j.status === 'published').length, color: 'blue' },
    { icon: '📬', label: 'Total Applications', value: hrStats.total_applications || applications.length, color: 'purple' },
    { icon: '📅', label: 'Interviews Today', value: hrStats.interviews_scheduled || 0, color: 'orange' },
    { icon: '🤝', label: 'Offers Made', value: hrStats.offers_made || 0, color: 'teal' },
    { icon: '✅', label: 'Joined This Year', value: hrStats.joined || 0, color: 'green' },
    { icon: '🎓', label: 'Training Programs', value: trainings.filter(t => t.status === 'ongoing').length, color: 'yellow' },
  ];

  return (
    <div>
      {/* ── Header ────────────────────────────────────────────────── */}
      <div className="page-header">
        <div>
          <h2 style={{ margin: 0, fontWeight: 800 }}>👔 HR Management</h2>
          <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>Recruitment, training, appraisals, policies & staff documents</p>
        </div>
        <div className="d-flex gap-2">
          {tab === 'recruitment'  && <button className="btn btn-primary" onClick={() => { setEditJob(null); setJobForm(BLANK_JOB); setShowJobForm(true); }}>➕ Post Job</button>}
          {tab === 'training'     && <button className="btn btn-primary" onClick={() => { setEditTrn(null); setTrnForm(BLANK_TRN); setShowTrnForm(true); }}>➕ New Training</button>}
          {tab === 'appraisals'   && <button className="btn btn-primary" onClick={() => { setAprForm(BLANK_APR); setShowAprForm(true); }}>➕ New Appraisal</button>}
          {tab === 'policies'     && <button className="btn btn-primary" onClick={() => { setEditPol(null); setPolForm(BLANK_POL); setShowPolForm(true); }}>➕ New Policy</button>}
        </div>
      </div>

      {/* ── Stats ─────────────────────────────────────────────────── */}
      <div className="row g-3 mb-4">
        {statCards.map(s => (
          <div key={s.label} className="col-6 col-md-4 col-lg-2">
            <StatCard icon={s.icon} label={s.label} value={s.value} color={s.color} />
          </div>
        ))}
      </div>

      {/* ── Tab bar ───────────────────────────────────────────────── */}
      <div className="d-flex gap-2 mb-4 flex-wrap">
        {TABS.map(t => (
          <button key={t.key} className={`btn btn-sm ${tab === t.key ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTab(t.key)}>
            {t.label} <span className="badge ms-1" style={{ background: tab === t.key ? 'rgba(255,255,255,0.3)' : '#e2e8f0', color: tab === t.key ? '#fff' : '#475569' }}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════════
          TAB: RECRUITMENT
      ══════════════════════════════════════════════════════════════ */}
      {tab === 'recruitment' && (
        <div>
          <div className="card mb-3">
            <div className="card-body">
              <div className="row g-2">
                <div className="col-md-4"><SearchBar onSearch={setSearch} placeholder="Search job title, department…" /></div>
                <div className="col-md-3">
                  <select className="form-select" value={jobStaF} onChange={e => setJobStaF(e.target.value)}>
                    <option value="">All Status</option>
                    <option value="draft">Draft</option><option value="published">Published</option>
                    <option value="closed">Closed</option><option value="on_hold">On Hold</option><option value="filled">Filled</option>
                  </select>
                </div>
                <div className="col-md-2"><button className="btn btn-outline-secondary w-100" onClick={() => { setSearch(''); setJobStaF(''); }}>🔄 Reset</button></div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-header"><span>Job Postings ({jobs.length})</span></div>
            {loading ? <div className="loading-spinner"><div className="spinner-border text-primary" /></div> :
              !jobs.length ? <div className="empty-state"><div className="empty-icon">🔍</div><h5>No job postings</h5><p>Create your first job posting to start recruiting</p></div> : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead style={{ background: '#f8fafc', fontSize: 12, fontWeight: 700, color: '#475569' }}>
                    <tr><th>Job Code</th><th>Title & Department</th><th>Type</th><th>Vacancies</th><th>Experience</th><th>Salary Range</th><th>Deadline</th><th>Applications</th><th>Status</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {jobs.map(j => (
                      <tr key={j.id}>
                        <td><code style={{ fontSize: 11, fontWeight: 700 }}>{j.job_code}</code></td>
                        <td>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{j.title}</div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>🏢 {j.department}{j.location && ` · 📍 ${j.location}`}</div>
                        </td>
                        <td><span className="badge bg-light text-dark">{j.employment_type.replace('_', ' ')}</span></td>
                        <td style={{ textAlign: 'center', fontWeight: 700 }}>{j.vacancies}</td>
                        <td style={{ fontSize: 12 }}>{j.experience_min}–{j.experience_max || '∞'} yrs</td>
                        <td style={{ fontSize: 12 }}>{j.salary_from && j.salary_to ? `${formatCurrency(j.salary_from)} – ${formatCurrency(j.salary_to)}` : '—'}</td>
                        <td style={{ fontSize: 12 }}>{j.application_deadline ? formatDate(j.application_deadline) : '—'}</td>
                        <td style={{ textAlign: 'center' }}>
                          <span style={{ fontWeight: 700, color: '#1d4ed8', cursor: 'pointer' }} onClick={() => setTab('applications')}>{j.applications_count || 0}</span>
                        </td>
                        <td><Chip map={JOB_STA} val={j.status} /></td>
                        <td>
                          <div className="d-flex gap-1">
                            <button className="btn btn-sm btn-outline-primary" onClick={() => openEditJob(j)}>✏️</button>
                            <button className="btn btn-sm btn-outline-danger" onClick={() => { if (window.confirm('Delete job posting?')) hrAPI.deleteJob(j.id).then(() => { toast.success('Deleted'); load(); }); }}>🗑️</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          TAB: APPLICATIONS
      ══════════════════════════════════════════════════════════════ */}
      {tab === 'applications' && (
        <div>
          <div className="card mb-3">
            <div className="card-body">
              <div className="row g-2">
                <div className="col-md-4"><SearchBar onSearch={setSearch} placeholder="Search applicant name, email…" /></div>
                <div className="col-md-3">
                  <select className="form-select" value={appStaF} onChange={e => setAppStaF(e.target.value)}>
                    <option value="">All Status</option>
                    {Object.keys(APP_STA).map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div className="col-md-2"><button className="btn btn-outline-secondary w-100" onClick={() => { setSearch(''); setAppStaF(''); }}>🔄</button></div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-header"><span>Applications ({applications.length})</span></div>
            {!applications.length ? <div className="empty-state"><div className="empty-icon">📄</div><h5>No applications yet</h5></div> : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead style={{ background: '#f8fafc', fontSize: 12, fontWeight: 700, color: '#475569' }}>
                    <tr><th>Applicant</th><th>Position</th><th>Experience</th><th>Current CTC</th><th>Expected CTC</th><th>Notice Period</th><th>Applied On</th><th>Interview</th><th>Score</th><th>Status</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {applications.map(a => (
                      <tr key={a.id}>
                        <td>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{a.applicant_name}</div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>📧 {a.email} · 📞 {a.phone}</div>
                          {a.current_employer && <div style={{ fontSize: 11, color: '#94a3b8' }}>💼 {a.current_employer} · {a.current_designation}</div>}
                        </td>
                        <td style={{ fontSize: 12 }}>{a.job_title}<br /><span style={{ color: '#94a3b8' }}>{a.job_department}</span></td>
                        <td style={{ fontSize: 12 }}>{a.experience_years} yrs</td>
                        <td style={{ fontSize: 12 }}>{a.current_ctc ? formatCurrency(a.current_ctc) : '—'}</td>
                        <td style={{ fontSize: 12 }}>{a.expected_ctc ? formatCurrency(a.expected_ctc) : '—'}</td>
                        <td style={{ fontSize: 12 }}>{a.notice_period_days} days</td>
                        <td style={{ fontSize: 12 }}>{formatDate(a.applied_date)}</td>
                        <td style={{ fontSize: 12 }}>{a.interview_date ? formatDate(a.interview_date) : '—'}{a.interview_mode && <div style={{ fontSize: 10, color: '#94a3b8' }}>{a.interview_mode}</div>}</td>
                        <td style={{ textAlign: 'center' }}>
                          {a.score ? <span style={{ fontWeight: 800, color: a.score >= 70 ? '#065f46' : a.score >= 50 ? '#92400e' : '#991b1b', fontSize: 15 }}>{a.score}</span> : '—'}
                        </td>
                        <td><Chip map={APP_STA} val={a.status} /></td>
                        <td><button className="btn btn-sm btn-outline-primary" onClick={() => openAppUpdate(a)}>🔄 Update</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          TAB: TRAINING
      ══════════════════════════════════════════════════════════════ */}
      {tab === 'training' && (
        <div>
          <div className="row g-3">
            {trainings.map(t => (
              <div key={t.id} className="col-md-6 col-lg-4">
                <div style={{ background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,.08)', height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div>
                      <Chip map={TRN_STA} val={t.status} />
                      <span className="badge bg-light text-dark ms-2" style={{ fontSize: 11 }}>{t.training_type.replace('_', ' ')}</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>{t.mode}</div>
                  </div>
                  <h6 style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>{t.title}</h6>
                  <div style={{ fontSize: 12, color: '#475569', flex: 1 }}>
                    {[
                      ['🎤 Trainer', `${t.trainer_name} (${t.trainer_type})`],
                      ['📅 Dates', `${formatDate(t.start_date)} → ${formatDate(t.end_date)}`],
                      ['⏱️ Duration', `${t.duration_hours} hours`],
                      ['📍 Venue', t.venue || 'Online / TBD'],
                      ['👥 Participants', `${t.enrollments_count || 0} / ${t.max_participants}`],
                      ['💰 Cost/Person', formatCurrency(t.cost_per_person)],
                    ].map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                        <span style={{ color: '#94a3b8', minWidth: 110 }}>{k}</span>
                        <span style={{ fontWeight: 500 }}>{v}</span>
                      </div>
                    ))}
                    {t.certificate_provided && <div style={{ color: '#065f46', fontWeight: 600, fontSize: 11, marginTop: 6 }}>🏅 Certificate Provided</div>}
                  </div>
                  <div className="d-flex gap-2 mt-3">
                    <button className="btn btn-sm btn-outline-primary flex-grow-1" onClick={() => openEditTrn(t)}>✏️ Edit</button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => {
                      const staffId = window.prompt('Staff ID to enroll:');
                      const staffName = window.prompt('Staff Name:');
                      if (staffId && staffName) hrAPI.createEnrollment({ program: t.id, staff_id: staffId, staff_name: staffName }).then(() => { toast.success('Enrolled!'); load(); }).catch(() => toast.error('Failed'));
                    }}>+ Enroll</button>
                  </div>
                </div>
              </div>
            ))}
            {!trainings.length && !loading && (
              <div className="col-12"><div className="empty-state"><div className="empty-icon">🎓</div><h5>No training programs</h5><p>Create training programs to upskill your staff</p></div></div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          TAB: APPRAISALS
      ══════════════════════════════════════════════════════════════ */}
      {tab === 'appraisals' && (
        <div className="card">
          <div className="card-header"><span>Performance Appraisals ({appraisals.length})</span></div>
          {!appraisals.length ? <div className="empty-state"><div className="empty-icon">⭐</div><h5>No appraisals recorded</h5></div> : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead style={{ background: '#f8fafc', fontSize: 12, fontWeight: 700, color: '#475569' }}>
                  <tr><th>Staff Name</th><th>Appraisal Period</th><th>Date</th><th>Teaching</th><th>Punctuality</th><th>Results</th><th>Teamwork</th><th>Overall</th><th>Increment%</th><th>Promotion</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {appraisals.map(a => {
                    const overall = a.overall_rating || ((Number(a.teaching_quality) + Number(a.punctuality) + Number(a.student_results) + Number(a.teamwork) + Number(a.communication) + Number(a.innovation)) / 6).toFixed(1);
                    return (
                      <tr key={a.id}>
                        <td style={{ fontWeight: 600 }}>{a.staff_name}</td>
                        <td style={{ fontSize: 12 }}>{a.appraisal_period}</td>
                        <td style={{ fontSize: 12 }}>{formatDate(a.appraisal_date)}</td>
                        {['teaching_quality', 'punctuality', 'student_results', 'teamwork'].map(k => (
                          <td key={k} style={{ textAlign: 'center' }}>
                            <span style={{ fontWeight: 700, color: a[k] >= 4 ? '#065f46' : a[k] >= 3 ? '#92400e' : '#991b1b' }}>{'⭐'.repeat(a[k] || 0)}</span>
                          </td>
                        ))}
                        <td style={{ textAlign: 'center' }}>
                          <span style={{ fontWeight: 800, fontSize: 15, color: overall >= 4 ? '#065f46' : overall >= 3 ? '#92400e' : '#991b1b' }}>{overall}/5</span>
                        </td>
                        <td style={{ textAlign: 'center', fontWeight: 700, color: '#1d4ed8' }}>{a.increment_recommended ? `${a.increment_recommended}%` : '—'}</td>
                        <td style={{ textAlign: 'center' }}>{a.promotion_recommended ? '✅ Yes' : '—'}</td>
                        <td><span style={{ background: a.status === 'finalized' ? '#d1fae5' : '#fef3c7', color: a.status === 'finalized' ? '#065f46' : '#92400e', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{a.status}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          TAB: POLICIES
      ══════════════════════════════════════════════════════════════ */}
      {tab === 'policies' && (
        <div>
          <div className="row g-2 mb-3">
            {['leave', 'conduct', 'salary', 'it', 'grievance', 'attendance', 'recruitment', 'travel', 'other'].map(cat => (
              <div key={cat} className="col-auto">
                <button className={`btn btn-sm ${polCatF === cat ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setPolCatF(polCatF === cat ? '' : cat)}>
                  {POL_CAT[cat]} {cat.replace('_', ' ')}
                </button>
              </div>
            ))}
          </div>
          <div className="row g-3">
            {policies.map(p => (
              <div key={p.id} className="col-md-6 col-lg-4">
                <div style={{ background: '#fff', borderRadius: 14, padding: 18, boxShadow: '0 2px 8px rgba(0,0,0,.08)', height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 24 }}>{POL_CAT[p.category]}</span>
                    <span style={{ fontSize: 10, background: '#f1f5f9', color: '#64748b', padding: '2px 8px', borderRadius: 20, fontWeight: 700 }}>v{p.version}</span>
                  </div>
                  <h6 style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>{p.title}</h6>
                  {p.summary && <p style={{ fontSize: 12, color: '#64748b', flex: 1, lineHeight: 1.5 }}>{p.summary}</p>}
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>
                    <div>📅 Effective: {formatDate(p.effective_date)}{p.review_date && ` · Review: ${formatDate(p.review_date)}`}</div>
                    <div>👥 Applicable to: {p.applicable_to.replace('_', ' ')}</div>
                  </div>
                  <div className="d-flex gap-2 mt-3">
                    <button className="btn btn-sm btn-outline-primary flex-grow-1" onClick={() => openEditPol(p)}>✏️ Edit</button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => { if (window.confirm('Delete policy?')) hrAPI.updatePolicy(p.id, { is_active: false }).then(() => { toast.success('Archived'); load(); }); }}>🗑️</button>
                  </div>
                </div>
              </div>
            ))}
            {!policies.length && !loading && <div className="col-12"><div className="empty-state"><div className="empty-icon">📜</div><h5>No policies defined</h5></div></div>}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          TAB: STAFF DOCUMENTS
      ══════════════════════════════════════════════════════════════ */}
      {tab === 'documents' && (
        <div className="card">
          <div className="card-header"><span>Staff Documents ({staffDocs.length})</span></div>
          {!staffDocs.length ? <div className="empty-state"><div className="empty-icon">📁</div><h5>No staff documents</h5></div> : (
            <div className="table-responsive">
              <table className="table mb-0">
                <thead style={{ background: '#f8fafc', fontSize: 12, fontWeight: 700, color: '#475569' }}>
                  <tr><th>Staff Name</th><th>Document Type</th><th>Title</th><th>Issue Date</th><th>Expiry Date</th><th>Issuing Authority</th><th>Uploaded</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {staffDocs.map(d => (
                    <tr key={d.id}>
                      <td style={{ fontWeight: 600 }}>{d.staff_name}</td>
                      <td><span className="badge bg-light text-dark">{d.document_type.replace('_', ' ')}</span></td>
                      <td style={{ fontSize: 13 }}>{d.title}</td>
                      <td style={{ fontSize: 12 }}>{d.issue_date ? formatDate(d.issue_date) : '—'}</td>
                      <td style={{ fontSize: 12, color: d.expiry_date && new Date(d.expiry_date) < new Date() ? '#b91c1c' : 'inherit' }}>{d.expiry_date ? formatDate(d.expiry_date) : '—'}</td>
                      <td style={{ fontSize: 12 }}>{d.issuing_authority || '—'}</td>
                      <td style={{ fontSize: 12 }}>{formatDate(d.uploaded_at)}</td>
                      <td><a href={d.file} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary">📥 Download</a></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ════════════════════ MODALS ════════════════════════════════ */}

      {/* Job Form Modal */}
      {showJobForm && (
        <Modal title={editJob ? `✏️ Edit Job — ${editJob.job_code}` : '➕ Post New Job'} onClose={() => setShowJobForm(false)} size="xl">
          <form onSubmit={saveJob}>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-md-6"><label className="form-label fw-semibold">Job Title *</label><input className="form-control" value={jobForm.title} onChange={e => JF('title', e.target.value)} required placeholder="e.g. Mathematics Teacher" /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Department *</label><input className="form-control" value={jobForm.department} onChange={e => JF('department', e.target.value)} required placeholder="e.g. Science" /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Employment Type</label>
                  <select className="form-select" value={jobForm.employment_type} onChange={e => JF('employment_type', e.target.value)}>
                    <option value="full_time">Full Time</option><option value="part_time">Part Time</option><option value="contract">Contract</option><option value="internship">Internship</option>
                  </select>
                </div>
                <div className="col-md-2"><label className="form-label fw-semibold">Vacancies</label><input type="number" className="form-control" value={jobForm.vacancies} onChange={e => JF('vacancies', +e.target.value)} min={1} /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Min Experience (yrs)</label><input type="number" className="form-control" value={jobForm.experience_min} onChange={e => JF('experience_min', +e.target.value)} min={0} /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Max Experience (yrs)</label><input type="number" className="form-control" value={jobForm.experience_max} onChange={e => JF('experience_max', e.target.value)} placeholder="Leave blank for no limit" /></div>
                <div className="col-md-2"><label className="form-label fw-semibold">Salary From ₹</label><input type="number" className="form-control" value={jobForm.salary_from} onChange={e => JF('salary_from', e.target.value)} /></div>
                <div className="col-md-2"><label className="form-label fw-semibold">Salary To ₹</label><input type="number" className="form-control" value={jobForm.salary_to} onChange={e => JF('salary_to', e.target.value)} /></div>
                <div className="col-md-4"><label className="form-label fw-semibold">Location</label><input className="form-control" value={jobForm.location} onChange={e => JF('location', e.target.value)} placeholder="e.g. Mumbai, Maharashtra" /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Posted Date *</label><input type="date" className="form-control" value={jobForm.posted_date} onChange={e => JF('posted_date', e.target.value)} required /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Application Deadline</label><input type="date" className="form-control" value={jobForm.application_deadline} onChange={e => JF('application_deadline', e.target.value)} /></div>
                <div className="col-md-2"><label className="form-label fw-semibold">Status</label>
                  <select className="form-select" value={jobForm.status} onChange={e => JF('status', e.target.value)}>
                    <option value="draft">Draft</option><option value="published">Publish Now</option><option value="closed">Closed</option><option value="on_hold">On Hold</option>
                  </select>
                </div>
                <div className="col-12"><label className="form-label fw-semibold">Skills Required</label><input className="form-control" value={jobForm.skills_required} onChange={e => JF('skills_required', e.target.value)} placeholder="comma, separated, skills" /></div>
                <div className="col-12"><label className="form-label fw-semibold">Job Description *</label><textarea className="form-control" rows={4} value={jobForm.description} onChange={e => JF('description', e.target.value)} required placeholder="Full job description…" /></div>
                <div className="col-md-6"><label className="form-label fw-semibold">Key Responsibilities</label><textarea className="form-control" rows={3} value={jobForm.responsibilities} onChange={e => JF('responsibilities', e.target.value)} /></div>
                <div className="col-md-6"><label className="form-label fw-semibold">Requirements</label><textarea className="form-control" rows={3} value={jobForm.requirements} onChange={e => JF('requirements', e.target.value)} /></div>
                <div className="col-md-6"><label className="form-label fw-semibold">Educational Qualifications</label><textarea className="form-control" rows={2} value={jobForm.qualifications} onChange={e => JF('qualifications', e.target.value)} /></div>
                <div className="col-md-6"><label className="form-label fw-semibold">Benefits & Perks</label><textarea className="form-control" rows={2} value={jobForm.benefits} onChange={e => JF('benefits', e.target.value)} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setShowJobForm(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</> : editJob ? '💾 Update Job' : '📢 Post Job'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Application Update Modal */}
      {showAppForm && selectedApp && (
        <Modal title={`🔄 Update Application — ${selectedApp.applicant_name}`} onClose={() => setShowAppForm(false)} size="lg">
          <form onSubmit={saveAppUpdate}>
            <div className="modal-body">
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: 14, marginBottom: 16, fontSize: 13 }}>
                <div style={{ fontWeight: 700 }}>{selectedApp.applicant_name}</div>
                <div style={{ color: '#64748b' }}>{selectedApp.job_title} · {selectedApp.experience_years} yrs exp · {selectedApp.email}</div>
              </div>
              <div className="row g-3">
                <div className="col-md-4"><label className="form-label fw-semibold">Application Status *</label>
                  <select className="form-select" value={appForm.status} onChange={e => setAppForm(f => ({ ...f, status: e.target.value }))}>
                    {Object.keys(APP_STA).map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div className="col-md-4"><label className="form-label fw-semibold">Interview Date & Time</label><input type="datetime-local" className="form-control" value={appForm.interview_date} onChange={e => setAppForm(f => ({ ...f, interview_date: e.target.value }))} /></div>
                <div className="col-md-4"><label className="form-label fw-semibold">Interview Mode</label>
                  <select className="form-select" value={appForm.interview_mode} onChange={e => setAppForm(f => ({ ...f, interview_mode: e.target.value }))}>
                    <option value="in_person">In Person</option><option value="video">Video Call</option><option value="phone">Phone</option>
                  </select>
                </div>
                <div className="col-md-4"><label className="form-label fw-semibold">Interview Score (1–100)</label><input type="number" className="form-control" value={appForm.score} onChange={e => setAppForm(f => ({ ...f, score: e.target.value }))} min={0} max={100} /></div>
                <div className="col-md-8"><label className="form-label fw-semibold">Internal Notes</label><input className="form-control" value={appForm.notes} onChange={e => setAppForm(f => ({ ...f, notes: e.target.value }))} placeholder="Interviewer observations…" /></div>
                {['rejected', 'offer_declined', 'withdrawn'].includes(appForm.status) && (
                  <div className="col-12"><label className="form-label fw-semibold">Reason</label><textarea className="form-control" rows={2} value={appForm.rejection_reason} onChange={e => setAppForm(f => ({ ...f, rejection_reason: e.target.value }))} /></div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setShowAppForm(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : '💾 Update Application'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Training Form Modal */}
      {showTrnForm && (
        <Modal title={editTrn ? '✏️ Edit Training Program' : '➕ New Training Program'} onClose={() => setShowTrnForm(false)} size="xl">
          <form onSubmit={saveTrn}>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-md-6"><label className="form-label fw-semibold">Program Title *</label><input className="form-control" value={trnForm.title} onChange={e => TF('title', e.target.value)} required /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Training Type</label>
                  <select className="form-select" value={trnForm.training_type} onChange={e => TF('training_type', e.target.value)}>
                    <option value="induction">Induction / Onboarding</option><option value="technical">Technical Skills</option>
                    <option value="soft_skills">Soft Skills</option><option value="compliance">Compliance / Safety</option>
                    <option value="leadership">Leadership</option><option value="pedagogy">Teaching Pedagogy</option><option value="other">Other</option>
                  </select>
                </div>
                <div className="col-md-3"><label className="form-label fw-semibold">Status</label>
                  <select className="form-select" value={trnForm.status} onChange={e => TF('status', e.target.value)}>
                    <option value="planned">Planned</option><option value="ongoing">Ongoing</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="col-md-4"><label className="form-label fw-semibold">Trainer Name *</label><input className="form-control" value={trnForm.trainer_name} onChange={e => TF('trainer_name', e.target.value)} required /></div>
                <div className="col-md-2"><label className="form-label fw-semibold">Trainer Type</label>
                  <select className="form-select" value={trnForm.trainer_type} onChange={e => TF('trainer_type', e.target.value)}>
                    <option value="internal">Internal</option><option value="external">External</option>
                  </select>
                </div>
                <div className="col-md-3"><label className="form-label fw-semibold">Training Provider</label><input className="form-control" value={trnForm.training_provider} onChange={e => TF('training_provider', e.target.value)} /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Delivery Mode</label>
                  <select className="form-select" value={trnForm.mode} onChange={e => TF('mode', e.target.value)}>
                    <option value="classroom">Classroom</option><option value="online">Online</option><option value="hybrid">Hybrid</option>
                  </select>
                </div>
                <div className="col-md-3"><label className="form-label fw-semibold">Start Date *</label><input type="date" className="form-control" value={trnForm.start_date} onChange={e => TF('start_date', e.target.value)} required /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">End Date *</label><input type="date" className="form-control" value={trnForm.end_date} onChange={e => TF('end_date', e.target.value)} required /></div>
                <div className="col-md-2"><label className="form-label fw-semibold">Duration (hrs)</label><input type="number" className="form-control" value={trnForm.duration_hours} onChange={e => TF('duration_hours', e.target.value)} step="0.5" /></div>
                <div className="col-md-4"><label className="form-label fw-semibold">Venue / URL</label><input className="form-control" value={trnForm.venue} onChange={e => TF('venue', e.target.value)} /></div>
                <div className="col-md-2"><label className="form-label fw-semibold">Max Participants</label><input type="number" className="form-control" value={trnForm.max_participants} onChange={e => TF('max_participants', +e.target.value)} min={1} /></div>
                <div className="col-md-2"><label className="form-label fw-semibold">Cost / Person ₹</label><input type="number" className="form-control" value={trnForm.cost_per_person} onChange={e => TF('cost_per_person', e.target.value)} /></div>
                <div className="col-md-2"><label className="form-label fw-semibold">Certificate?</label>
                  <div className="mt-2"><label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}><input type="checkbox" checked={trnForm.certificate_provided} onChange={e => TF('certificate_provided', e.target.checked)} /> Provide Certificate</label></div>
                </div>
                <div className="col-12"><label className="form-label fw-semibold">Description</label><textarea className="form-control" rows={3} value={trnForm.description} onChange={e => TF('description', e.target.value)} /></div>
                <div className="col-md-6"><label className="form-label fw-semibold">Objectives</label><textarea className="form-control" rows={2} value={trnForm.objectives} onChange={e => TF('objectives', e.target.value)} /></div>
                <div className="col-md-6"><label className="form-label fw-semibold">Notes</label><textarea className="form-control" rows={2} value={trnForm.notes} onChange={e => TF('notes', e.target.value)} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setShowTrnForm(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : editTrn ? '💾 Update' : '🎓 Create Program'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Appraisal Form Modal */}
      {showAprForm && (
        <Modal title="⭐ New Performance Appraisal" onClose={() => setShowAprForm(false)} size="xl">
          <form onSubmit={saveApr}>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-md-3"><label className="form-label fw-semibold">Staff ID *</label><input className="form-control" value={aprForm.staff_id} onChange={e => AF('staff_id', e.target.value)} required /></div>
                <div className="col-md-5"><label className="form-label fw-semibold">Staff Name *</label><input className="form-control" value={aprForm.staff_name} onChange={e => AF('staff_name', e.target.value)} required /></div>
                <div className="col-md-4"><label className="form-label fw-semibold">Appraisal Period *</label><input className="form-control" value={aprForm.appraisal_period} onChange={e => AF('appraisal_period', e.target.value)} required placeholder="e.g. 2024-25 Annual" /></div>
                <div className="col-md-4"><label className="form-label fw-semibold">Appraisal Date *</label><input type="date" className="form-control" value={aprForm.appraisal_date} onChange={e => AF('appraisal_date', e.target.value)} required /></div>
                <div className="col-12"><div style={{ fontWeight: 700, fontSize: 13, color: '#334155', padding: '8px 0 4px', borderBottom: '1px solid #e2e8f0' }}>📊 KPI Ratings (1=Poor → 5=Excellent)</div></div>
                {[['teaching_quality', '🎓 Teaching Quality'], ['punctuality', '⏰ Punctuality & Attendance'], ['student_results', '📈 Student Results'], ['teamwork', '🤝 Teamwork & Collaboration'], ['communication', '💬 Communication'], ['innovation', '💡 Innovation & Initiative']].map(([k, label]) => (
                  <div key={k} className="col-md-4">
                    <label className="form-label fw-semibold">{label}</label>
                    <div className="d-flex gap-2">
                      {[1, 2, 3, 4, 5].map(n => (
                        <button key={n} type="button" style={{ width: 40, height: 40, borderRadius: 8, border: `2px solid ${aprForm[k] === n ? '#1a3a5c' : '#e2e8f0'}`, background: aprForm[k] === n ? '#1a3a5c' : '#fff', color: aprForm[k] === n ? '#fff' : '#475569', fontWeight: 700, cursor: 'pointer' }} onClick={() => AF(k, n)}>{n}</button>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="col-12"><label className="form-label fw-semibold">Key Achievements</label><textarea className="form-control" rows={2} value={aprForm.achievements} onChange={e => AF('achievements', e.target.value)} /></div>
                <div className="col-md-6"><label className="form-label fw-semibold">Areas of Improvement</label><textarea className="form-control" rows={2} value={aprForm.areas_of_improvement} onChange={e => AF('areas_of_improvement', e.target.value)} /></div>
                <div className="col-md-6"><label className="form-label fw-semibold">Training Recommendations</label><textarea className="form-control" rows={2} value={aprForm.training_recommendations} onChange={e => AF('training_recommendations', e.target.value)} /></div>
                <div className="col-md-6"><label className="form-label fw-semibold">Goals for Next Period</label><textarea className="form-control" rows={2} value={aprForm.goals_for_next_period} onChange={e => AF('goals_for_next_period', e.target.value)} /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Increment Recommended (%)</label><input type="number" className="form-control" value={aprForm.increment_recommended} onChange={e => AF('increment_recommended', e.target.value)} step="0.5" /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Promotion Recommended?</label>
                  <div className="mt-2"><label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}><input type="checkbox" checked={aprForm.promotion_recommended} onChange={e => AF('promotion_recommended', e.target.checked)} /> Yes, recommend promotion</label></div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setShowAprForm(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : '⭐ Save Appraisal'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Policy Form Modal */}
      {showPolForm && (
        <Modal title={editPol ? '✏️ Edit HR Policy' : '📜 New HR Policy'} onClose={() => setShowPolForm(false)} size="xl">
          <form onSubmit={savePol}>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-md-6"><label className="form-label fw-semibold">Policy Title *</label><input className="form-control" value={polForm.title} onChange={e => PF('title', e.target.value)} required /></div>
                <div className="col-md-2"><label className="form-label fw-semibold">Category *</label>
                  <select className="form-select" value={polForm.category} onChange={e => PF('category', e.target.value)}>
                    {Object.entries(POL_CAT).map(([k, ic]) => <option key={k} value={k}>{ic} {k}</option>)}
                  </select>
                </div>
                <div className="col-md-2"><label className="form-label fw-semibold">Version</label><input className="form-control" value={polForm.version} onChange={e => PF('version', e.target.value)} placeholder="1.0" /></div>
                <div className="col-md-2"><label className="form-label fw-semibold">Applicable To</label>
                  <select className="form-select" value={polForm.applicable_to} onChange={e => PF('applicable_to', e.target.value)}>
                    <option value="all">All Staff</option><option value="teaching">Teaching Staff</option><option value="non_teaching">Non-Teaching</option>
                  </select>
                </div>
                <div className="col-md-3"><label className="form-label fw-semibold">Effective Date *</label><input type="date" className="form-control" value={polForm.effective_date} onChange={e => PF('effective_date', e.target.value)} required /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Review Date</label><input type="date" className="form-control" value={polForm.review_date} onChange={e => PF('review_date', e.target.value)} /></div>
                <div className="col-12"><label className="form-label fw-semibold">Summary (Quick Reference)</label><textarea className="form-control" rows={2} value={polForm.summary} onChange={e => PF('summary', e.target.value)} placeholder="One-paragraph summary…" /></div>
                <div className="col-12"><label className="form-label fw-semibold">Full Policy Content *</label><textarea className="form-control" rows={8} value={polForm.content} onChange={e => PF('content', e.target.value)} required placeholder="Complete policy text…" /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setShowPolForm(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : editPol ? '💾 Update Policy' : '📜 Create Policy'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
